import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtError } from "@/lib/api";
import { Plus, Save, Trash2, Upload, Download } from "lucide-react";

/**
 * CRUD admin pour un catalogue (matériel ou véhicules).
 * Deux modes :
 *  - kind="equipment" : items groupés par catégorie
 *  - kind="vehicles"  : liste plate (pas de catégorie)
 * Import CSV en masse : upsert par `code` (création ou mise à jour).
 */
export default function AdminCatalog({ kind }) {
  const base = kind === "equipment" ? "/admin/equipment/catalog" : "/admin/vehicles/catalog";
  const withCat = kind === "equipment";
  const testKey = kind;
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(base); setItems(data); }
    catch (err) { toast.error(fmtError(err)); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({
    code: "", label: "", categorie: withCat ? "" : undefined,
    order: (items.length + 1) * 10, active: true,
  });
  const startEdit = (it) => setEditing({ ...it });
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const payload = {
      code: (editing.code || "").trim(),
      label: (editing.label || "").trim(),
      categorie: withCat ? (editing.categorie || "").trim() || "Autre" : null,
      order: Number(editing.order) || 0,
      active: !!editing.active,
    };
    setBusy(true);
    try {
      if (editing.id) {
        await api.put(`${base}/${editing.id}`, payload);
        toast.success("Élément mis à jour");
      } else {
        await api.post(base, payload);
        toast.success("Élément créé");
      }
      setEditing(null); load();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cet élément ?")) return;
    try { await api.delete(`${base}/${id}`); toast.success("Élément supprimé"); load(); }
    catch (err) { toast.error(fmtError(err)); }
  };

  const onFile = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) { toast.error("Fichier trop volumineux (512 Ko max)"); return; }
    const text = await file.text();
    setCsvText(text);
  };

  const runImport = async () => {
    setImportBusy(true); setImportResult(null);
    try {
      const { data } = await api.post(`${base}/import`, { csv: csvText });
      setImportResult(data);
      const total = (data.created || 0) + (data.updated || 0);
      if (total > 0) {
        toast.success(`${data.created} créé(s), ${data.updated} mis à jour`);
        load();
      }
      if ((data.errors || []).length) {
        toast.warning(`${data.errors.length} erreur(s) dans le CSV — voir le détail`);
      }
    } catch (err) { toast.error(fmtError(err)); }
    finally { setImportBusy(false); }
  };

  const csvExample = withCat
    ? "code,label,categorie,order,active\nsec.masque_p3,Masque P3,Sécurité et protection,710,true\nmes.testeur_electrique,Testeur électrique,Mesure et contrôle,650,true"
    : "code,label,order,active\nveh.utilitaire_frigo,Utilitaire frigorifique,70,true\nveh.camion_grue,Camion-grue,80,true";
  const downloadExample = () => {
    const blob = new Blob([csvExample + "\n"], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `exemple-import-${kind}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const closeImport = () => { setShowImport(false); setCsvText(""); setImportResult(null); };

  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap justify-end gap-2">
        <button onClick={() => setShowImport(true)} className="btn-outline" data-testid={`${testKey}-import-open`}>
          <Upload size={14} /> Importer CSV
        </button>
        <button onClick={startNew} className="btn-primary" data-testid={`${testKey}-add`}>
          <Plus size={14} /> Nouvel élément
        </button>
      </div>

      {showImport && (
        <div className="card p-4 space-y-3 border-2 border-[#1B3A5C]" data-testid={`${testKey}-import-panel`}>
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-[#1F2933]">Import CSV en masse</div>
            <button onClick={closeImport} className="btn-ghost text-xs" data-testid={`${testKey}-import-close`}>Fermer</button>
          </div>
          <p className="text-xs text-[#3E4C59]">
            Colonnes attendues (dans cet ordre, ou avec en-tête) :{" "}
            <code className="font-mono bg-[#F5F6F7] px-1 rounded">
              {withCat ? "code,label,categorie,order,active" : "code,label,order,active"}
            </code>. Comportement : <strong>upsert par code</strong> — les codes existants sont mis à jour, les nouveaux créés. Max 500 lignes par import.
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={downloadExample} className="btn-outline text-xs" data-testid={`${testKey}-import-example`}>
              <Download size={12} /> Télécharger un exemple
            </button>
            <label className="btn-outline text-xs cursor-pointer">
              <Upload size={12} /> Charger un fichier .csv
              <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid={`${testKey}-import-file`} />
            </label>
          </div>
          <textarea
            className="input font-mono text-xs min-h-[160px] resize-y"
            placeholder={csvExample}
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            data-testid={`${testKey}-import-textarea`}
          />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={closeImport} className="btn-outline">Annuler</button>
            <button
              type="button"
              onClick={runImport}
              disabled={importBusy || !csvText.trim()}
              className="btn-primary"
              data-testid={`${testKey}-import-submit`}
            >
              <Upload size={14} /> {importBusy ? "…" : "Lancer l'import"}
            </button>
          </div>
          {importResult && (
            <div className="mt-2 p-3 bg-[#F5F6F7] rounded-[4px] text-sm" data-testid={`${testKey}-import-result`}>
              <div>
                <span className="badge-valid">{importResult.created} créé(s)</span>{" "}
                <span className="badge-warn">{importResult.updated} mis à jour</span>{" "}
                <span className="text-xs text-[#3E4C59]">sur {importResult.total_processed} ligne(s)</span>
              </div>
              {(importResult.errors || []).length > 0 && (
                <div className="mt-2">
                  <div className="text-xs uppercase tracking-wider text-[#C0392B] font-semibold">Erreurs</div>
                  <ul className="mt-1 text-xs list-disc list-inside space-y-0.5">
                    {importResult.errors.map((e, i) => (
                      <li key={i}>Ligne {e.line} : {e.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {editing && (
        <form onSubmit={save} className="card p-4 space-y-3" data-testid={`${testKey}-form`}>
          <div className="text-sm font-semibold text-[#1F2933]">
            {editing.id ? "Modifier l'élément" : "Nouvel élément"}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="label">Code (stable, sert d'identifiant)</label>
              <input className="input font-mono text-xs" required maxLength={80}
                value={editing.code}
                onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                data-testid={`${testKey}-code`} />
            </div>
            <div>
              <label className="label">Libellé (visible utilisateur)</label>
              <input className="input" required maxLength={200}
                value={editing.label}
                onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                data-testid={`${testKey}-label`} />
            </div>
            {withCat && (
              <div>
                <label className="label">Catégorie</label>
                <input className="input" maxLength={100} placeholder="ex. Découpe"
                  value={editing.categorie || ""}
                  onChange={(e) => setEditing({ ...editing, categorie: e.target.value })}
                  data-testid={`${testKey}-categorie`} />
              </div>
            )}
            <div>
              <label className="label">Ordre d'affichage</label>
              <input type="number" className="input" value={editing.order}
                onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                data-testid={`${testKey}-order`} />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  data-testid={`${testKey}-active`} />
                Actif (visible côté utilisateur)
              </label>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={cancel} className="btn-outline">Annuler</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid={`${testKey}-save`}>
              <Save size={14} /> {busy ? "…" : "Enregistrer"}
            </button>
          </div>
        </form>
      )}

      <div className="card overflow-x-auto">
        <table className="table-compact w-full">
          <thead>
            <tr>
              {withCat && <th>Catégorie</th>}
              <th>Ordre</th><th>Code</th><th>Libellé</th><th>Actif</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={withCat ? 6 : 5} className="text-center text-[#3E4C59]">Aucun élément</td></tr>
            )}
            {items.map(it => (
              <tr key={it.id} data-testid={`${testKey}-row-${it.id}`}>
                {withCat && <td>{it.categorie}</td>}
                <td className="font-mono text-xs">{it.order}</td>
                <td className="font-mono text-xs">{it.code}</td>
                <td>{it.label}</td>
                <td>{it.active
                  ? <span className="badge-valid">Actif</span>
                  : <span className="badge-muted">Désactivé</span>}</td>
                <td className="flex gap-1">
                  <button onClick={() => startEdit(it)} className="btn-outline text-xs" data-testid={`${testKey}-edit-${it.id}`}>Modifier</button>
                  <button onClick={() => remove(it.id)} className="btn-danger text-xs" data-testid={`${testKey}-delete-${it.id}`}>
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
